import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import { forwardRef } from "react";
import * as t from "@rekajs/types";
import type { ComponentType } from "@repo/editor/types";
import { Link } from "@repo/i18n/routing";
import { useApp } from "@repo/app/useApp";
import { observer } from "mobx-react-lite";
import { ArrowButton } from "../components/common/arrow-button";
import { cn } from "../libs/utils";
import VideoPlayer from "../components/common/video-player";

export const TextImageSectionDefinition: ComponentSettingsProps = {
  labelType: {
    label: "نوع تایتل",
    type: "enum",
    section: "تایتل",
    options: [
      { value: "h1", label: "h1" },
      { value: "h2", label: "h2" },
      { value: "h3", label: "h3" },
      { value: "h4", label: "h4" },
      { value: "h5", label: "h5" },
      { value: "h6", label: "h6" },
      { value: "p", label: "p" },
      { value: "span", label: "span" },
    ],
    default: "h3",
  },
  label: {
    label: "متن تایتل",
    section: "تایتل",
    type: "string",
    default: "متن شما",
  },
  image: {
    label: "تصویر",
    type: "image",
    section: "تصویر",
  },
  labelColor: {
    label: "رنگ تایتل",
    type: "colorPicker",
    section: "تایتل",
    default: "#7E7E7E",
  },
  showDescription: {
    label: "نمایش توضیحات",
    type: "boolean",
    section: "توضیحات",
    default: true,
  },
  description: {
    label: "متن توضیحات",
    section: "توضیحات",
    type: "html",
  },
  showDescription2: {
    label: "نمایش توضیحات 2",
    type: "boolean",
    section: "توضیحات",
  },
  description2: {
    label: "متن توضیحات 2",
    section: "توضیحات",
    type: "html",
  },
  number: {
    label: "شماره",
    type: "string",
    section: "بخش",
  },
  textNumber: {
    label: "متن شماره",
    type: "string",
    section: "بخش",
  },
  moreButtonPosition: {
    label: "موقعیت دکمه بیشتر",
    type: "enum",
    section: "دکمه بیشتر",
    options: [
      { value: "left", label: "چپ" },
      { value: "right", label: "راست" },
      { value: "center", label: "مرکز" },
    ],
    default: "right",
  },
  direction: {
    label: "جهت",
    type: "enum",
    options: [
      { value: "left-right", label: "از مست چپ به راست" },
      { value: "right-left", label: "از سمت راست به چپ" },
    ],
    default: "right-left",
  },
  sideText: {
    label: "متن سمت",
    type: "string",
    section: "متن سمت",
  },
  alt: {
    type: "string",
    label: "عنوان تصویر",
    default: "تصویر",
  },
  link: {
    type: "string",
    label: "لینک",
  },
};

export interface TextImageSectionProps {
  label: string;
  labelType?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
  labelColor: string;
  description: string;
  description2: string;
  showDescription: boolean;
  showDescription2: boolean;
  moreButtonPosition: "left" | "right" | "center";
  image: string;
  alt?: string;
  link?: string;
  direction: "left-right" | "right-left";
  sideText?: string;
  number?: number;
  textNumber?: string;
}

const TextImageComponent = forwardRef<HTMLAnchorElement, TextImageSectionProps>(
  (props, ref) => {
    const app = useApp();
    const isEditor = app.rendererMode === "editor";
    return (
      <>
        {props.sideText ? (
          <div className="absolute ltr:left-10 rtl:right-10 top-3/4 lg:flex hidden">
            <div className="size-full relative flex items-center justify-center">
              <div className="absolute rtl:rotate-90 ltr:-rotate-90 font-thin lg:text-3xl text-xl px-2 text-gray-300 before:content-[''] before:inline-block before:bg-gray-300 before:h-px before:lg:w-14 before:w-10 after:content-[''] after:inline-block after:bg-gray-300 after:h-px after:lg:w-14 after:w-10 flex items-center">
                <span className="mx-2 uppercase rtl:-mt-3 whitespace-nowrap rtl:font-doran rtl:font-medium">
                  {props.sideText}
                </span>
              </div>
            </div>
          </div>
        ) : null}
        <Link
          className="group w-full h-full relative"
          href={props.link || "#"}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            if (isEditor) {
              e.preventDefault();
            }
          }}
          ref={ref}
        >
          {props.number ? (
            <div className="absolute top-4 transform -translate-y-1/2 -left-16 w-full h-full justify-start hidden lg:flex">
              <div className="relative col-span-12 lg:col-span-4 flex items-center justify-center">
                <h1 className="font-helvetica text-[#ff770033] text-[190px] font-[100] rtl:font-bold rtl:font-dirooz leading-[1.07] tracking-[-8.56px] lg:text-[250px] lg:tracking-[-15.71px] text-center">
                  {props.number}
                </h1>
                <p className="font-helvetica rtl:font-doran absolute top-1/2 -translate-y-20 text-[29px] lg:text-[40px] font-[100] rtl:font-bold leading-[1.07] text-[#6a6a6a] rtl:text-[#f70] uppercase">
                  {props.textNumber}
                </p>
              </div>
            </div>
          ) : null}
          <div
            className={cn(
              "w-full flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-10",
              props.direction === "left-right"
                ? "lg:flex-row-reverse items-start"
                : "lg:flex-row items-end"
            )}
          >
            <div className={cn("basis-8/12 w-8/12 lg:w-full lg:basis-5/12")}>
              {props.image && props.image.includes("mp4") ? (
                <VideoPlayer link={props.image || ""} />
              ) : (
                <img
                  alt={
                    props.alt || String(TextImageSectionDefinition.alt.default)
                  }
                  className="w-full h-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/images/placeholder.png";
                  }}
                  src={props.image}
                />
              )}
            </div>
            <div
              className={cn(
                "basis-auto lg:max-w-6/12 flex flex-col gap-[20px] md:gap-[20px] lg:gap-[40px]"
              )}
            >
              {props.labelType ? (
                <props.labelType
                  className="rtl:font-doran text-[24px] md:text-[40px] md:tracking-[0.2px] font-[500] tracking-[0.12px] mb-0 pb-0 "
                  style={{
                    color:
                      props.labelColor ||
                      String(TextImageSectionDefinition.labelColor.default),
                  }}
                >
                  {props.label ||
                    String(TextImageSectionDefinition.label.default)}
                </props.labelType>
              ) : (
                <h3
                  className="rtl:font-doran text-[24px] md:text-[40px] md:tracking-[0.2px] font-[500] tracking-[0.12px] mb-0 pb-0 "
                  style={{
                    color:
                      props.labelColor ||
                      String(TextImageSectionDefinition.labelColor.default),
                  }}
                >
                  {props.label ||
                    String(TextImageSectionDefinition.label.default)}
                </h3>
              )}
              {(
                typeof props.showDescription !== "boolean"
                  ? TextImageSectionDefinition.showDescription.default
                  : props.showDescription
              ) ? (
                <div
                  className="rtl:font-doran md:text-[32px] md:tracking-[0.32px] text-[19.5px] font-light leading-[1.56] tracking-[0.2px] py-0 my-0 ql-editor ql-editor"
                  dangerouslySetInnerHTML={{
                    __html: props.description,
                  }}
                />
              ) : null}
              {(
                typeof props.showDescription2 !== "boolean"
                  ? TextImageSectionDefinition.showDescription2.default
                  : props.showDescription2
              ) ? (
                <div
                  className="rtl:font-doran md:text-[32px] md:tracking-[0.32px] text-[19.5px] font-light leading-[1.56] tracking-[0.2px] py-0 my-0"
                  dangerouslySetInnerHTML={{
                    __html: props.description2,
                  }}
                />
              ) : null}
              <div
                className={cn(
                  "flex",
                  // eslint-disable-next-line no-nested-ternary -- moreButtonPosition
                  props.moreButtonPosition === "center"
                    ? "justify-center"
                    : props.moreButtonPosition === "right"
                      ? "justify-end"
                      : "justify-start"
                )}
              >
                <div className="w-32 pl-24 rtl:pr-24">
                  <ArrowButton />
                </div>
              </div>
            </div>
          </div>
        </Link>
      </>
    );
  }
);

TextImageComponent.displayName = "TextImageSection";

export const TextImage = observer(
  TextImageComponent
) as unknown as ComponentType<TextImageSectionProps>;

export const TextImageSectionSettings: ComponentSettings = {
  name: "ETextImageSection",
  props: TextImageSectionDefinition,
  label: "بخش متن و عکس",
  widgets: {
    label: "بخش متن و عکس",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "ETextImageSection", external: true }),
      }),
  },
};

TextImage.componentSettings = TextImageSectionSettings;
